import { useMemo, useState } from 'react';
import {
  Checkbox,
  InlineStack,
  SegmentedControl,
  Select,
  Tag,
  Text,
  Tooltip,
} from '@customerio/pluma-components/react';
import type { AttributeListScope } from '@/services/db/attribute/entity';
import { useAttributeCatalog } from '@/services/db/attribute/queries';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';

const SEARCH_DEBOUNCE_MS = 250;

type RequiredAttributeTagsProps = {
  attributes: readonly string[];
};

/**
 * Locked, always-included attribute tags, rendered in the attribute Select's
 * leading slot so they read as the first entries of the selection. The
 * tooltip uses its custom-reference mode: the default mode wraps the child in
 * a focusable button, which is invalid inside the Select's trigger button.
 */
function RequiredAttributeTags({ attributes }: RequiredAttributeTagsProps) {
  return (
    <InlineStack gap="050" alignBlock="center">
      {attributes.map((attribute) => (
        <Tooltip key={attribute} content="This attribute is always included in exports." placement="top" isCustom>
          {(context) => (
            <span ref={context.reference.setReference} {...context.reference.getReferenceProps()}>
              <Tag isDisabled>{attribute}</Tag>
            </span>
          )}
        </Tooltip>
      ))}
    </InlineStack>
  );
}

type AttributeMultiPickerProps = {
  workspaceId: number;
  objectTypeId?: string;
  /** "people attributes" or "<Type> attributes" — drives the placeholders. */
  attributeNoun: string;
  requiredAttributes: readonly string[];
  hiddenAttributes: readonly string[];
  /** The current explicit selection (required attributes included). */
  selected: readonly string[];
  onChange: (attributes: string[]) => void;
};

/**
 * The searchable attribute multiselect: server-side search and paging over
 * the attribute catalog, locked tags for the required attributes, and the
 * "only attributes in use" filter scoping the picker.
 */
function AttributeMultiPicker({
  workspaceId,
  objectTypeId,
  attributeNoun,
  requiredAttributes,
  hiddenAttributes,
  selected,
  onChange,
}: AttributeMultiPickerProps) {
  const [search, setSearch] = useState('');
  const [inUseOnly, setInUseOnly] = useState(false);
  const [debouncedSearch] = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const catalogScope: AttributeListScope = { search: debouncedSearch };
  if (inUseOnly) catalogScope.inUseOnly = true;
  if (objectTypeId != null) catalogScope.objectTypeId = objectTypeId;
  const catalog = useAttributeCatalog(workspaceId, catalogScope);
  const catalogAttributes = useMemo(() => catalog.data?.pages.flat() ?? [], [catalog.data]);

  // The first page of a new scope replaces; while it loads, load-more is
  // parked so a late page can't append under the previous scope.
  const isReloading = catalog.isFetching && !catalog.isFetchingNextPage;

  // The multiselect's value — the selection minus the locked required ones.
  const optionalSelected = useMemo(
    () => selected.filter((attribute) => !requiredAttributes.includes(attribute)),
    [selected, requiredAttributes],
  );

  // Options: the fetched pages plus any already-selected attributes, minus
  // required/hidden. Selected attributes are merged in so their tokens keep
  // rendering even when a filter (e.g. "in use only") would otherwise drop
  // them from the fetched page.
  const options = useMemo(() => {
    const values = new Set([...catalogAttributes, ...optionalSelected]);
    return [...values]
      .filter((attribute) => !requiredAttributes.includes(attribute) && !hiddenAttributes.includes(attribute))
      .map((attribute) => ({ value: attribute, label: attribute }));
  }, [catalogAttributes, optionalSelected, requiredAttributes, hiddenAttributes]);

  return (
    <>
      <Select
        mt="150"
        isMulti
        isSearchable
        canWrap
        ariaLabel="Attributes to add"
        placeholder={`Select ${attributeNoun}`}
        searchPlaceholder={`Search ${attributeNoun}`}
        elementBefore={
          requiredAttributes.length > 0 ? <RequiredAttributeTags attributes={requiredAttributes} /> : undefined
        }
        value={[...optionalSelected]}
        options={options}
        onChange={(values: string[] | null) => onChange([...requiredAttributes, ...(values ?? [])])}
        withVirtualizer
        searchInputValue={search}
        onSearchInput={(event) => {
          if (event.currentTarget instanceof HTMLInputElement) {
            setSearch(event.currentTarget.value);
          }
        }}
        isLoading={isReloading}
        onLoadMore={() => {
          if (catalog.isFetching || catalog.hasNextPage === false) return;
          void catalog.fetchNextPage();
        }}
        shouldLoadMore={catalog.hasNextPage && !isReloading}
        isLoadingMore={catalog.isFetchingNextPage}
        onCloseComplete={() => {
          // Clear the search when the dropdown closes so the next open starts fresh.
          setSearch('');
        }}
      />

      {/* A filter on the picker above, not a selection mode — kept tight to the search it scopes. */}
      <Checkbox
        value="in_use_only"
        label="Only show attributes in use"
        isChecked={inUseOnly}
        onCheckedChange={setInUseOnly}
        isDisabled={isReloading}
        mt="100"
      />
    </>
  );
}

export type ExportAttributePanelProps = {
  workspaceId: number;
  /** When set, offers that object type's attributes; otherwise people attributes. */
  objectTypeId?: string;
  /** The object type's display name — drives the picker placeholders. */
  objectTypeName?: string;
  requiredAttributes: readonly string[];
  /** Attributes to never offer (e.g. internal id columns always present in the file). */
  hiddenAttributes?: readonly string[];
  /** The selected attributes, or null for "all attributes". */
  selectedAttributes: readonly string[] | null;
  onChange: (attributes: string[] | null) => void;
};

/**
 * Attribute picker for one export section (People, or one object type). A
 * segmented control switches between "All attributes" and a searchable
 * multiselect of removable tokens, with required attributes shown as locked
 * tags. Search and pagination are server-side (50 per page); the "only
 * attributes in use" checkbox scopes the picker.
 */
export function ExportAttributePanel({
  workspaceId,
  objectTypeId,
  objectTypeName,
  requiredAttributes,
  hiddenAttributes = [],
  selectedAttributes,
  onChange,
}: ExportAttributePanelProps) {
  const allMode = selectedAttributes === null;
  const selected = selectedAttributes ?? requiredAttributes;
  const attributeNoun = objectTypeName == null ? 'people attributes' : `${objectTypeName} attributes`;

  return (
    <div>
      <SegmentedControl
        name={objectTypeId == null ? 'export-mode-people' : `export-mode-${objectTypeId}`}
        ariaLabel={`Choose which ${attributeNoun} to include`}
        value={allMode ? 'all' : 'select'}
        options={[
          { label: 'All attributes', value: 'all' },
          { label: 'Select attributes', value: 'select' },
        ]}
        onChange={(mode) => onChange(mode === 'all' ? null : [...selected])}
      />

      {allMode ? (
        <Text display="block" color="subtle" mt="150">
          Every attribute is included in the export.
        </Text>
      ) : (
        <AttributeMultiPicker
          workspaceId={workspaceId}
          objectTypeId={objectTypeId}
          attributeNoun={attributeNoun}
          requiredAttributes={requiredAttributes}
          hiddenAttributes={hiddenAttributes}
          selected={selected}
          onChange={onChange}
        />
      )}
    </div>
  );
}
